import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatFileSizeLabel, type StoryImportDraft } from "@/features/story-import/story-import.types";

interface StoryImportUploadStepProps {
  disabled?: boolean;
  draft: StoryImportDraft | null;
  fileSizeLimitBytes: number;
  maxPageCount: number;
  isUploading: boolean;
  onSelectFile: (file: File) => void;
}

export function StoryImportUploadStep({
  disabled = false,
  draft,
  fileSizeLimitBytes,
  maxPageCount,
  isUploading,
  onSelectFile,
}: StoryImportUploadStepProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  function isFileDrag(event: DragEvent<HTMLElement>) {
    return Array.from(event.dataTransfer?.types ?? []).includes("Files");
  }

  function handleFileSelection(file: File | null | undefined) {
    if (!file || disabled || isUploading) {
      return;
    }

    onSelectFile(file);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    handleFileSelection(file);
    event.currentTarget.value = "";
  }

  return (
    <Card>
      <CardContent className="space-y-6 p-8">
        <div className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-clay">
            {t("storyImport.upload.eyebrow")}
          </p>
          <h2 className="text-3xl text-foreground">{t("storyImport.upload.title")}</h2>
          <p className="max-w-3xl text-base leading-7 text-muted-foreground">
            {t("storyImport.upload.body")}
          </p>
        </div>

        <div
          className={cn(
            "flex flex-col items-center justify-center gap-4 rounded-[2rem] border border-dashed px-6 py-12 text-center transition",
            isDraggingFile
              ? "border-ink/35 bg-white shadow-vellum"
              : "border-ink/15 bg-white/65 hover:border-ink/30 hover:bg-white",
          )}
          data-testid="story-import-dropzone"
          onDragEnter={(event) => {
            if (!isFileDrag(event)) {
              return;
            }

            event.preventDefault();
            setIsDraggingFile(true);
          }}
          onDragLeave={(event) => {
            if (!isFileDrag(event)) {
              return;
            }

            event.preventDefault();
            if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
              return;
            }
            setIsDraggingFile(false);
          }}
          onDragOver={(event) => {
            if (!isFileDrag(event)) {
              return;
            }

            event.preventDefault();
            event.dataTransfer.dropEffect = disabled || isUploading ? "none" : "copy";
            setIsDraggingFile(true);
          }}
          onDrop={(event) => {
            if (!isFileDrag(event)) {
              return;
            }

            event.preventDefault();
            setIsDraggingFile(false);
            handleFileSelection(event.dataTransfer.files?.[0]);
          }}
        >
          <Upload className="h-10 w-10 text-clay" />
          <div className="space-y-1">
            <p className="text-base font-semibold text-foreground">
              {t("storyImport.upload.dropzoneTitle")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("storyImport.upload.maxSize", {
                size: formatFileSizeLabel(fileSizeLimitBytes),
              })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("storyImport.upload.maxPages", {
                count: maxPageCount,
              })}
            </p>
          </div>
          <Button
            disabled={disabled || isUploading}
            onClick={() => {
              inputRef.current?.click();
            }}
            type="button"
            variant="outline"
          >
            {isUploading
              ? t("storyImport.actions.uploading")
              : t("storyImport.actions.chooseFile")}
          </Button>
          <input
            accept=".docx,.pdf,.txt,.md"
            className="sr-only"
            disabled={disabled || isUploading}
            onChange={handleInputChange}
            ref={inputRef}
            type="file"
          />
        </div>

        {draft ? (
          <div className="rounded-[1.5rem] border border-ink/10 bg-white/70 px-5 py-4">
            <p className="text-sm font-semibold text-foreground">{draft.fileName}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {draft.detectedFormat.toUpperCase()} · {formatFileSizeLabel(draft.fileSizeBytes)}
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
